import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const Gallery = ({ session }) => {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    const { data, error } = await supabase.storage.from('regatta-photos').list('', {
      limit: 100,
      sortBy: { column: 'created_at', order: 'desc' } // show newest photos first
    });

    if (data) {
      const imageDataList = data
        // Filter out empty placeholders Supabase might create
        .filter(file => file.name !== '.emptyFolderPlaceholder')
        .map(file => {
          const { data: publicUrlData } = supabase.storage.from('regatta-photos').getPublicUrl(file.name);
          
          let uploaderName = "Sailor";
          if (file.name.includes('--by--')) {
            const parts = file.name.split('--by--');
            if (parts.length > 1) {
               uploaderName = decodeURIComponent(parts[1].split('-')[0].replace(/_/g, ' '));
            }
          }

          return { url: publicUrlData.publicUrl, poster: uploaderName };
        });
      setImages(imageDataList);
    } else {
      console.error("Error loading images", error);
    }
  };

  const handleUpload = async (event) => {
    if (!session) {
      alert("You must be registered and logged in as a member to upload photos!");
      return;
    }

    const file = event.target.files[0];
    if (!file) return;

    // 🔥 Added Strict 2MB File Size Limit to save Supabase free tier space
    if (file.size > 2 * 1024 * 1024) {
      alert("Whoa! That photo is massive. For testing, please select an image smaller than 2MB so you don't use up your database limit!");
      return;
    }

    setUploading(true);
    
    // Grab the skipper name, replacing spaces with underscores so URL is clean
    const rawSkipper = session.user.user_metadata?.skipper_name || 'Anonymous';
    const safeSkipperName = encodeURIComponent(rawSkipper.replace(/ /g, '_'));

    // Generate a secure unique file name, embedding the skipper name in the middle to extract later!
    const fileName = `${Date.now()}--by--${safeSkipperName}-${file.name.replace(/[^a-zA-Z0-9.]/g, '')}`;
    
    const { error } = await supabase.storage.from('regatta-photos').upload(fileName, file, {
      cacheControl: '3600',
      upsert: false
    });

    if (error) {
      alert("Upload failed! Make sure your Supabase Storage Bucket allows public uploads. Error: " + error.message);
    } else {
      // Refresh the gallery instantly!
      await fetchImages();
      alert("Photo uploaded successfully! ⛵");
    }
    
    setUploading(false);
  };

  return (
    <div className="gallery-container" style={{ padding: '20px' }}>
      <h3>Regatta Photo Gallery</h3>
      
      {/* Upload Section - Only rendered elegantly if they are logged in */}
      <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #ddd' }}>
        {session ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <label style={{ 
              padding: '10px 20px', backgroundColor: '#0056b3', color: 'white', 
              borderRadius: '5px', cursor: uploading ? 'not-allowed' : 'pointer',
              fontWeight: 'bold', display: 'inline-block'
            }}>
              {uploading ? "Uploading..." : "📸 Upload Photo"}
              <input 
                type="file" 
                accept="image/*" 
                onChange={handleUpload} 
                disabled={uploading} 
                style={{ display: 'none' }} // visually hidden but accessible
              />
            </label>
            <span style={{ fontSize: '14px', color: '#666' }}>
              Members may share limited photos with the public.
            </span>
          </div>
        ) : (
           <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
             <i>(You must be logged in to contribute photos to this gallery.)</i>
           </p>
        )}
      </div>

      {/* The Photo Grid */}
      {images.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#999', margin: '40px 0' }}>The gallery is currently empty.</p>
      ) : (
        <div className="photo-grid" style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '20px'
        }}>
          {images.map((imgObj, index) => (
            <div key={index} style={{ display: 'flex', flexDirection: 'column', height: '240px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #ddd', backgroundColor: '#fff', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <div style={{ height: '195px', width: '100%' }}>
                <img 
                  src={imgObj.url} 
                  alt="Regatta Community Upload" 
                  title={`Photo by ${imgObj.poster}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                />
              </div>
              <div style={{ padding: '10px', fontSize: '13px', color: '#555', backgroundColor: '#f8f9fa', textAlign: 'center', borderTop: '1px solid #eee', fontWeight: 'bold' }}>
                📸 Posted by: <span style={{ color: '#0056b3' }}>{imgObj.poster}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Gallery;
